import { getAuditorControlStatus } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { cn } from '@/lib/utils'
import {
  TestDeferralBadge,
  type TestDeferralKind,
} from '@/components/compliance/TestInclusionChip'

// ── Single source of truth for test / control effective status ──

export type EffectiveStatus = {
  key: string
  label: string
  bg: string
  text: string
  border: string
  dotColor: string
  stripeClassName: string
  /** Token for the shared `Badge` primitive, so chips don't re-derive colours. */
  badge: 'default' | 'green' | 'amber' | 'blue' | 'red' | 'purple'
}

export const EFFECTIVE_STATUSES: Record<string, EffectiveStatus> = {
  UNAVAILABLE: {
    key: 'UNAVAILABLE', label: 'Status unavailable', bg: 'bg-surface-muted',
    text: 'text-text-subtle', border: 'border-border', dotColor: 'bg-status-gray',
    stripeClassName: 'border-l-status-gray', badge: 'default',
  },
  OUT_OF_SCOPE: {
    key: 'OUT_OF_SCOPE',
    label: 'Out of Scope',
    bg: 'bg-status-gray-surface-subtle',
    text: 'text-text-subtle',
    border: 'border-status-gray-border-subtle',
    dotColor: 'bg-status-gray',
    stripeClassName: 'border-l-border-divider',
    badge: 'default',
  },
  PENDING_SUBMISSION: {
    key: 'PENDING_SUBMISSION',
    label: 'Pending submission',
    bg: 'bg-surface-muted',
    text: 'text-text-subtle',
    border: 'border-border',
    dotColor: 'bg-border-divider',
    stripeClassName: 'border-l-status-gray',
    badge: 'default',
  },
  PENDING_REVIEW: {
    key: 'PENDING_REVIEW',
    label: 'Pending review',
    bg: 'bg-status-orange-bg',
    text: 'text-status-amber-strong',
    border: 'border-status-orange/30',
    dotColor: 'bg-status-orange',
    stripeClassName: 'border-l-status-orange',
    badge: 'amber',
  },
  REVIEWED: {
    key: 'REVIEWED',
    label: 'Reviewed',
    bg: 'bg-status-blue-subtle',
    text: 'text-status-blue-text',
    border: 'border-status-blue-border',
    dotColor: 'bg-status-blue',
    stripeClassName: 'border-l-status-blue',
    badge: 'blue',
  },
  SATISFACTORY: {
    key: 'SATISFACTORY',
    label: 'Satisfactory',
    bg: 'bg-status-green-bg',
    text: 'text-status-green',
    border: 'border-status-green/30',
    dotColor: 'bg-status-green',
    stripeClassName: 'border-l-status-green',
    badge: 'green',
  },
  EXCEPTION_NOTED: {
    key: 'EXCEPTION_NOTED',
    label: 'Exception noted',
    bg: 'bg-status-red-bg',
    text: 'text-status-red',
    border: 'border-status-red/30',
    dotColor: 'bg-status-red',
    stripeClassName: 'border-l-status-red',
    badge: 'red',
  },
  NEEDS_REMEDIATION: {
    key: 'NEEDS_REMEDIATION',
    label: 'Needs remediation',
    bg: 'bg-status-orange-subtle',
    text: 'text-status-orange',
    border: 'border-status-orange/30',
    dotColor: 'bg-status-orange',
    stripeClassName: 'border-l-status-orange',
    badge: 'amber',
  },
}

export function AuditorControlStatusBadge({ control, className }: {
  control: Parameters<typeof getAuditorControlStatus>[0]
  className?: string
}) {
  const status = EFFECTIVE_STATUSES[getAuditorControlStatus(control)]
  return (
    <Badge status={status.badge} className={cn(
      status.badge === 'amber' && 'bg-status-orange text-white dark:text-neutral-950',
      status.badge === 'green' && 'bg-status-green text-white',
      status.badge === 'red' && 'bg-status-red text-white',
      className,
    )}
      title={control.auditorReview?.reReviewRequestedAt ? 'Company approved remediation for another auditor review' : control.auditorReview?.stale ? 'Evidence or scope changed since the last auditor conclusion' : undefined}>
      {status.label}
    </Badge>
  )
}

/**
 * Is this Test outside the client's active working set?
 *
 * The tree has always SHIPPED deactivated Tests — "13 (2 N/A)" counts them —
 * but the review column rendered them exactly like live work, so the two the
 * client scoped out looked like two more submissions that had not arrived yet.
 * `scopeState` is what separates them; `status` is the fallback for the surfaces
 * whose queries don't carry it (Open Requests).
 */
export function isTestDeactivated(test: {
  scopeState?: string | null
  status?: string | null
}): boolean {
  if (test.scopeState) return test.scopeState !== 'ACTIVE'
  return test.status === 'NOT_APPLICABLE' || test.status === 'ON_HOLD'
}

/**
 * Deferred by THIS engagement's framework, not by the client — the tuple is
 * core only because of another framework they are enrolled in (`coreFor`,
 * synthesized per cycle in `auditorScopeView`).
 *
 * Nothing is proposable here. The test is already active in the client's
 * programme, so "Propose activate" would be rejected by the server as already
 * in scope; and "Propose defer" is the exact harm this deferral prevents — an
 * accepted defer writes `scopeSource: MANUAL`, which every reconcile skips, so
 * a SOC 2 decision would strip the evidence the other framework requires.
 */
export function isOutOfFrameworkTest(test: {
  deferralKind?: string | null
}): boolean {
  return test.deferralKind === 'OUT_OF_FRAMEWORK'
}

/**
 * Why the Test is out of scope, in the auditor's vocabulary.
 *
 * A deferral is a roadmap item — one client click from active — and reads as
 * "later"; a plain N/A is a verdict. Collapsing the two would hide the ones
 * actually worth asking back, which is the whole point of showing them here.
 * The deferral wording is the customer-side badge so both sides of the
 * engagement read the same label for the same Test.
 */
export function DeactivatedTestChip({
  test,
  className,
}: {
  test: { scopeState?: string | null; deferralKind?: string | null; deactivationReason?: string | null; status?: string | null }
  className?: string
}) {
  if (test.scopeState === 'DEFERRED' && test.deferralKind) {
    return (
      <TestDeferralBadge
        deferralKind={test.deferralKind as TestDeferralKind}
        deferralReason={test.deactivationReason}
        className={className}
      />
    )
  }
  return (
    // Amber, not grey — the same call the customer's own test page makes: an
    // excluded Test means no evidence will be collected for it, which is a
    // coverage gap someone chose and the auditor is the one who has to weigh
    // it. Grey reads as "nothing to see here", which is the opposite.
    <Badge
      status='amber'
      className={className}
      title={test.deactivationReason ?? undefined}
    >
      {test.scopeState === 'ON_HOLD' || test.status === 'ON_HOLD'
        ? 'On hold'
        : 'Not applicable'}
    </Badge>
  )
}

/**
 * The one badge a Test wears on every auditor surface (controls tree, key
 * documents): the auditor's own verdict when they have given one; otherwise
 * out-of-scope; otherwise whether the client has anything approved for the
 * auditor to look at yet.
 */
export function getTestEffectiveStatus(
  test: { reviewStatus?: string | null; scopeState?: string | null; status?: string | null },
  hasApprovedSubmission: boolean,
): EffectiveStatus {
  if (test.reviewStatus && EFFECTIVE_STATUSES[test.reviewStatus]) {
    return EFFECTIVE_STATUSES[test.reviewStatus]
  }
  if (isTestDeactivated(test)) return EFFECTIVE_STATUSES.OUT_OF_SCOPE
  return hasApprovedSubmission
    ? EFFECTIVE_STATUSES.PENDING_REVIEW
    : EFFECTIVE_STATUSES.PENDING_SUBMISSION
}

/**
 * #1425 — the Type II period-coverage gap, in the auditor's terms.
 *
 * Shown wherever a set of tests is summarised (a tree row, a pinned control
 * header) as well as on the test row itself: an auditor reviewing a Type II
 * is judging a window, and evidence that covers part of it is a finding.
 */
export function CoverageGapChip({
  count,
  worstDays,
  className,
}: {
  count: number
  worstDays?: number | null
  className?: string
}) {
  if (count <= 0) return null
  const worst = worstDays ?? null
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-sm bg-status-orange/12 px-1.5 py-0.5 text-[10px] font-semibold text-status-orange-strong',
        className,
      )}
      title={
        `${count} test${count === 1 ? '' : 's'} with a period-coverage gap` +
        (worst != null ? ` · worst ${worst} day${worst === 1 ? '' : 's'} uncovered` : '')
      }
    >
      {/* Worst gap, not the sum — gap days across tests do not share a window,
          so adding them up produces a number that means nothing. */}
      {worst != null ? `${worst}d gap` : `${count} gap${count === 1 ? '' : 's'}`}
      {count > 1 && <span className='font-normal opacity-70'>×{count}</span>}
    </span>
  )
}
